/**
 * ClinVea-network Referral workflow — backs /clinical-referrals on the backend
 * (ClinicalReferralController). NOT the same as `api.referrals` (modules/referrals.ts), which is
 * the unrelated marketing "Patient Referral Program" at /referrals. Same word, different feature —
 * see ClinicalReferralController's class comment before touching either.
 */
import { fetchApi, fetchGetFileApi, fetchMultipartApi } from '../client';
import { buildQS } from '../helpers';
import type { PagedResponse } from '../../../types/pagination';

export type ReferralDestinationType = 'CLINVEA' | 'EXTERNAL';

export type ReferralStatus =
  | 'DRAFT' | 'SUBMITTED' | 'SENT' | 'DELIVERED' | 'RECEIVED' | 'UNDER_REVIEW'
  | 'ACCEPTED' | 'DECLINED' | 'NEEDS_MORE_INFORMATION' | 'SCHEDULED'
  | 'PATIENT_SEEN' | 'REPORT_PENDING' | 'REPORT_RECEIVED' | 'COMPLETED'
  | 'CANCELLED' | 'EXPIRED';

export type ClinicalUrgency = 'ROUTINE' | 'URGENT' | 'EMERGENCY';

export type RecordShareCategory =
  | 'CLINICAL_SUMMARY' | 'DIAGNOSES' | 'MEDICATIONS' | 'ALLERGIES' | 'LAB_RESULTS'
  | 'IMAGING' | 'PRESCRIPTIONS' | 'CONSULTATION_NOTES' | 'PREVIOUS_PROCEDURES'
  | 'VITALS' | 'VISITS' | 'ENTIRE_HISTORY' | 'SENSITIVE_RECORDS';

/** How the patient's (or representative's) agreement was obtained. EMERGENCY_EXCEPTION is the
 *  documented "unable to consent" basis and needs the reason in the notes. */
export type PatientConsentMethod = 'VERBAL' | 'WRITTEN' | 'OTP' | 'PORTAL' | 'REPRESENTATIVE' | 'EMERGENCY_EXCEPTION';

export type ClinicalDocumentCategory =
  | 'REFERRAL_LETTER' | 'LAB_REPORT' | 'IMAGING' | 'ECG' | 'DISCHARGE_SUMMARY'
  | 'PRESCRIPTION' | 'CONSULTATION_REPORT' | 'CONSENT_FORM' | 'OTHER';

/** An attachment on a referral or transfer. Downloads go through the authenticated endpoint. */
export interface ClinicalDocument {
  id: string;
  fileName: string;
  contentType: string;
  fileSizeBytes?: number;
  documentCategory: ClinicalDocumentCategory;
  uploadedByClinicId: string;
  uploadedByClinicName?: string;
  uploadedByName?: string;
  uploadedAt: string;
  /** True for the uploading clinic while the case is open. */
  canRemove: boolean;
}

/** Builds the four attachment calls for /clinical-referrals or /clinical-transfers. */
export function documentsApi(base: string) {
  return {
    list: (id: string) => fetchApi<ClinicalDocument[]>(`${base}/${id}/documents`),
    upload: (id: string, file: File, category: ClinicalDocumentCategory) => {
      const form = new FormData();
      form.append('file', file);
      form.append('category', category);
      return fetchMultipartApi<ClinicalDocument>(`${base}/${id}/documents`, form);
    },
    download: (id: string, documentId: string) => fetchGetFileApi(`${base}/${id}/documents/${documentId}`),
    withdraw: (id: string, documentId: string, reason?: string) =>
      fetchApi<void>(`${base}/${id}/documents/${documentId}/withdraw`, { method: 'POST', body: JSON.stringify({ reason }) }),
  };
}

export interface ConsentCapture {
  patientConsentCaptured: boolean;
  consentMethod?: PatientConsentMethod;
  consentNotes?: string;
}

export interface ReferralReport {
  findings?: string;
  diagnosis?: string;
  icdCode?: string;
  treatment?: string;
  adviceToReferrer?: string;
  /** REFERRING_PROVIDER / SPECIALIST / BOTH / NONE */
  followUpWith?: string;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
}

export interface RecordShareSelection {
  id: string;
  categories: RecordShareCategory[];
  dateFrom?: string | null;
  dateTo?: string | null;
  createdBy: string;
  createdAt: string;
}

export interface Referral {
  id: string;
  referralNumber: string;
  patientId: string;
  patientName?: string;
  patientMobile?: string;
  patientDateOfBirth?: string;
  patientAge?: number;
  patientGender?: string;

  referringClinicId: string;
  referringClinicName?: string;
  referringProviderId?: string;
  referringProviderName?: string;
  referringProviderRegistrationNumber?: string;

  destinationType: ReferralDestinationType;
  destinationOrganizationId?: string;
  destinationOrganizationName?: string;
  destinationProviderId?: string;
  destinationProviderName?: string;
  externalOrganizationId?: string;
  externalOrganizationName?: string;
  externalProviderId?: string;
  externalProviderName?: string;

  specialty?: string;
  referralReason: string;
  clinicalSummary?: string;
  urgency: ClinicalUrgency;
  requestedService?: string;
  provisionalDiagnosis?: string;
  icdCode?: string;
  currentMedications?: string;
  allergies?: string;
  investigationsDone?: string;
  preferredDate?: string;

  patientConsentCaptured: boolean;
  consentMethod?: PatientConsentMethod;
  consentCapturedAt?: string;
  consentNotes?: string;

  status: ReferralStatus;
  appointmentId?: string;
  appointmentDate?: string;
  appointmentTimeSlot?: string;
  appointmentDoctorName?: string;
  /** The specialist's consultation report — null until submitted. */
  report?: ReferralReport | null;

  createdAt: string;
  submittedAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  scheduledAt?: string;
  patientSeenAt?: string;
  reportReceivedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  expiresAt?: string;

  incoming: boolean;
  allowedNextStatuses: ReferralStatus[];
  recordShare?: RecordShareSelection | null;
  messageCount: number;
}

export interface ReferralDashboard {
  outgoingTotal: number;
  outgoingPendingDecision: number;
  outgoingNeedsMoreInfo: number;
  outgoingScheduled: number;
  outgoingAwaitingReport: number;
  outgoingCompleted: number;
  incomingTotal: number;
  incomingNeedsReview: number;
  incomingAccepted: number;
  incomingScheduled: number;
  emergencyOrUrgentOpen: number;
}

export interface ReferralStatusHistoryEntry {
  id: string;
  referralId: string;
  fromStatus: ReferralStatus | null;
  toStatus: ReferralStatus;
  changedBy: string;
  reason?: string | null;
  changedAt: string;
}

export interface ReferralMessage {
  id: string;
  referralId: string;
  senderId: string;
  message: string;
  sentAt: string;
  readAt?: string | null;
}

export interface DestinationClinic {
  id: string;
  name: string;
  city?: string;
  state?: string;
}

export interface DestinationDoctor {
  id: string;
  name: string;
  specialization?: string;
  departmentId?: string;
}

export interface ExternalOrganization {
  id: string;
  name: string;
  organizationType?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  notes?: string;
  isActive: boolean;
}

export interface ExternalProvider {
  id: string;
  providerName: string;
  externalOrganizationId?: string;
  specialty?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  contactPerson?: string;
  notes?: string;
  isActive: boolean;
}

export interface ExternalOrganizationCreateRequest {
  name: string;
  organizationType?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  notes?: string;
}

export interface ExternalProviderCreateRequest {
  providerName: string;
  externalOrganizationId?: string;
  specialty?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  contactPerson?: string;
  notes?: string;
}

export interface ReferralCreateRequest {
  patientId: string;
  referringProviderId?: string;
  destinationType: ReferralDestinationType;
  destinationOrganizationId?: string;
  destinationProviderId?: string;
  externalOrganizationId?: string;
  externalProviderId?: string;
  specialty?: string;
  referralReason: string;
  clinicalSummary?: string;
  urgency: ClinicalUrgency;
  requestedService?: string;
  provisionalDiagnosis?: string;
  icdCode?: string;
  currentMedications?: string;
  allergies?: string;
  investigationsDone?: string;
  /** yyyy-MM-dd */
  preferredDate?: string;
  /** Required to submit (a draft can be saved without it). */
  patientConsentCaptured: boolean;
  consentMethod?: PatientConsentMethod;
  consentNotes?: string;
  recordShareCategories?: RecordShareCategory[];
  recordShareDateFrom?: string;
  recordShareDateTo?: string;
  submit: boolean;
}

/** Either link an appointment already booked at the desk, or book one now (the destination
 *  clinic's own patient record + doctor + date), or record a date only. */
export interface ReferralScheduleRequest {
  appointmentId?: string;
  scheduledAt?: string;
  localPatientId?: string;
  doctorId?: string;
  /** yyyy-MM-dd */
  visitDate?: string;
  timeSlot?: string;
  visitType?: 'NEW' | 'FOLLOWUP';
}

export interface ReferralReportRequest {
  findings: string;
  diagnosis?: string;
  icdCode?: string;
  treatment?: string;
  adviceToReferrer?: string;
  followUpWith?: string;
}

const BASE = '/api/clinical-referrals';

export const clinicalReferralsModule = {
  /** `patientId` narrows the clinic-scoped list server-side — used by the consultation Referrals tab. */
  listOutgoing: (status?: ReferralStatus, patientId?: string) =>
    fetchApi<Referral[]>(`${BASE}${buildQS({ status, patientId })}`),

  listIncoming: () =>
    fetchApi<Referral[]>(`${BASE}/incoming`),

  /** One page of outgoing referrals; status and search (number, specialty, patient name) run on the server (medopd-docs 112). */
  listOutgoingPage: (params: { status?: ReferralStatus; q?: string; patientId?: string; page: number; size: number }) =>
    fetchApi<PagedResponse<Referral>>(`${BASE}${buildQS(params)}`),

  /** One page of incoming referrals, searched on the server (medopd-docs 112). */
  listIncomingPage: (params: { q?: string; page: number; size: number }) =>
    fetchApi<PagedResponse<Referral>>(`${BASE}/incoming${buildQS(params)}`),

  getDashboard: () =>
    fetchApi<ReferralDashboard>(`${BASE}/dashboard`),

  searchDestinationClinics: (q: string) =>
    fetchApi<DestinationClinic[]>(`${BASE}/destinations/clinics${buildQS({ q })}`),

  listDestinationDoctors: (destinationClinicId: string) =>
    fetchApi<DestinationDoctor[]>(`${BASE}/destinations/clinics/${destinationClinicId}/doctors`),

  get: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}`),

  getTimeline: (id: string) =>
    fetchApi<ReferralStatusHistoryEntry[]>(`${BASE}/${id}/timeline`),

  create: (data: ReferralCreateRequest) =>
    fetchApi<Referral>(BASE, { method: 'POST', body: JSON.stringify(data) }),

  /** `consent` records the patient's consent on a draft saved without it. */
  submit: (id: string, consent?: ConsentCapture) =>
    fetchApi<Referral>(`${BASE}/${id}/submit`, { method: 'POST', body: JSON.stringify(consent ?? {}) }),

  accept: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/accept`, { method: 'POST' }),

  /** A reason is mandatory — the referrer needs it to re-route the patient. */
  decline: (id: string, reason: string) =>
    fetchApi<Referral>(`${BASE}/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),

  requestMoreInfo: (id: string, reason: string) =>
    fetchApi<Referral>(`${BASE}/${id}/request-more-info`, { method: 'POST', body: JSON.stringify({ reason }) }),

  schedule: (id: string, data: ReferralScheduleRequest) =>
    fetchApi<Referral>(`${BASE}/${id}/schedule`, { method: 'POST', body: JSON.stringify(data) }),

  /** The specialist's consultation report — closes the loop with content. */
  submitReport: (id: string, data: ReferralReportRequest) =>
    fetchApi<Referral>(`${BASE}/${id}/report`, { method: 'POST', body: JSON.stringify(data) }),

  /** The printable referral letter (PDF). */
  getLetter: (id: string) => fetchGetFileApi(`${BASE}/${id}/letter`),

  /** Attachments (ECG, imaging, reports) — both clinics can add and read them. */
  documents: documentsApi(BASE),

  markPatientSeen: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/patient-seen`, { method: 'POST' }),

  markReportPending: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/report-pending`, { method: 'POST' }),

  receiveReport: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/report-received`, { method: 'POST' }),

  complete: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/complete`, { method: 'POST' }),

  cancel: (id: string, reason?: string) =>
    fetchApi<Referral>(`${BASE}/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  listMessages: (id: string) =>
    fetchApi<ReferralMessage[]>(`${BASE}/${id}/messages`),

  addMessage: (id: string, message: string) =>
    fetchApi<ReferralMessage>(`${BASE}/${id}/messages`, { method: 'POST', body: JSON.stringify({ message }) }),

  getRecordShare: (id: string) =>
    fetchApi<RecordShareSelection | null>(`${BASE}/${id}/record-share`),

  setRecordShare: (id: string, data: { categories: RecordShareCategory[]; dateFrom?: string; dateTo?: string }) =>
    fetchApi<RecordShareSelection>(`${BASE}/${id}/record-share`, { method: 'POST', body: JSON.stringify(data) }),

  // ── External directory (providers/organizations outside ClinVea) ─────────

  searchExternalOrganizations: (q?: string) =>
    fetchApi<ExternalOrganization[]>(`${BASE}/external/organizations${buildQS({ q })}`),

  createExternalOrganization: (data: ExternalOrganizationCreateRequest) =>
    fetchApi<ExternalOrganization>(`${BASE}/external/organizations`, { method: 'POST', body: JSON.stringify(data) }),

  listExternalProvidersForOrganization: (organizationId: string) =>
    fetchApi<ExternalProvider[]>(`${BASE}/external/organizations/${organizationId}/providers`),

  searchExternalProviders: (q: string) =>
    fetchApi<ExternalProvider[]>(`${BASE}/external/providers${buildQS({ q })}`),

  createExternalProvider: (data: ExternalProviderCreateRequest) =>
    fetchApi<ExternalProvider>(`${BASE}/external/providers`, { method: 'POST', body: JSON.stringify(data) }),

  /** Edits an organisation; every field is replaced, so send the whole record. */
  updateExternalOrganization: (id: string, data: ExternalOrganizationCreateRequest) =>
    fetchApi<ExternalOrganization>(`${BASE}/external/organizations/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),

  /** Edits a provider; every field is replaced, so send the whole record. */
  updateExternalProvider: (id: string, data: ExternalProviderCreateRequest) =>
    fetchApi<ExternalProvider>(`${BASE}/external/providers/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
};
